// User roles hook with structured logging
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabaseClient';
import { supabaseRpcWithRetry, supabaseQueryWithRetry } from '@/lib/networkRetry';
import { createLogger } from '@/lib/structuredLogger';
import { useAuth } from './use-auth';
import { QUERY_KEYS } from '@/config/queries';

const logger = createLogger('UserRoles');

export type AppRole = 'user' | 'moderator' | 'admin' | 'super_admin' | 'company_admin';

const VALID_ROLES: AppRole[] = ['user', 'moderator', 'admin', 'super_admin', 'company_admin'];

const ROLE_PRIORITY: Record<AppRole, number> = {
  user: 0,
  company_admin: 1,
  moderator: 1,
  admin: 2,
  super_admin: 3,
};

const isValidRole = (role: string | null | undefined): role is AppRole => {
  return typeof role === 'string' && (VALID_ROLES as readonly string[]).includes(role);
};

export interface UserRole {
  id: string;
  user_id: string;
  role: AppRole;
  created_at: string;
}

/**
 * Fetch user roles using batched get_profile_data RPC
 * Falls back to direct user_roles query if RPC fails
 */
async function fetchUserRoles(userId: string): Promise<AppRole[]> {
  try {
    // Try batched RPC first
    const { data, error } = await supabaseRpcWithRetry<{ roles?: unknown }>(supabase, 'get_profile_data', {
      user_id_param: userId,
    });

    if (!error && Array.isArray(data?.roles)) {
      return data.roles.filter((role): role is AppRole => typeof role === 'string' && isValidRole(role));
    }

    // Fallback to direct query if RPC fails
    const { data: rolesData, error: rolesError } = await supabaseQueryWithRetry(() =>
      supabase.from('user_roles').select('role').eq('user_id', userId),
    );

    if (rolesError) {
      throw rolesError;
    }

    const roleRows = (rolesData ?? []) as Array<{ role?: unknown }>;
    return roleRows
      .map((r) => r.role)
      .filter((role): role is AppRole => typeof role === 'string' && isValidRole(role));
  } catch (error) {
    logger.error('Error fetching user roles', { error });
    return [];
  }
}

export const useUserRoles = () => {
  const { user, session } = useAuth();

  // Use React Query for caching (<200ms cache hits)
  const {
    data: roles = [],
    isLoading,
    refetch,
  } = useQuery({
    queryKey: QUERY_KEYS.USER_ROLES(user?.id ?? ''),
    queryFn: () => (user ? fetchUserRoles(user.id) : []),
    enabled: !!user && !!session,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const hasRole = useMemo(() => {
    return (requiredRole: AppRole): boolean => {
      if (!roles?.length) return false;
      const requiredPriority = ROLE_PRIORITY[requiredRole];
      return roles.some((role) => ROLE_PRIORITY[role] >= requiredPriority);
    };
  }, [roles]);

  const isAdmin = useMemo(() => hasRole('admin'), [hasRole]);
  const isSuperAdmin = useMemo(() => hasRole('super_admin'), [hasRole]);
  const isCompanyAdmin = useMemo(() => hasRole('company_admin'), [hasRole]);
  const isModerator = useMemo(() => hasRole('moderator'), [hasRole]);

  return {
    roles,
    loading: isLoading,
    hasRole,
    isAdmin,
    isSuperAdmin,
    isCompanyAdmin,
    isModerator,
    refetch: () => refetch(),
  };
};
